import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bell, Lock, Mail, Monitor, Moon, Palette, Smartphone,
  Sun, Tags,
} from 'lucide-react';
import { adminApi } from '../services/api';
import FormulesTarifaires from '../components/FormulesTarifaires';
import { annoncerErreur, annoncerSucces, EtatErreur, EtatSansDonnees, SqueletteListe } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   PARAMÈTRES

   Cette page ne contenait que le profil — identité, mot de passe, vignette —
   c'est-à-dire tout sauf des paramètres. Le profil est parti dans « Mon
   profil » ; ce qui reste ici est ce qu'on RÈGLE.

   Trois blocs, et les deux premiers existaient déjà sans interface :

   1. Le thème. Il était piloté depuis l'en-tête par une bascule à deux états,
      donc impossible à laisser suivre le système. L'option « Système » est
      ajoutée ici et dans `AdminLayout`, qui écoute désormais la préférence du
      navigateur.

   2. Les notifications. Le serveur porte depuis le début un catalogue de types
      (`TypeNotification`) et des préférences par personne et par canal
      (`PreferenceNotification`) que `notifier()` respecte scrupuleusement —
      mais rien, dans aucune des deux interfaces, ne permettait d'y toucher.
      Le réglage existait et n'était pas réglable.

   3. Les formules tarifaires. Elles étaient écrites en dur dans le serveur :
      corriger un prix demandait une livraison. Elles vivent maintenant en
      base et se modifient ici — c'est la seule partie de la page qui ne
      concerne pas la personne connectée mais toute la plateforme, et elle est
      séparée par son propre intertitre.

   Les types marqués « critiques » s'affichent verrouillés plutôt que masqués.
   Les cacher donnerait l'impression d'un réglage dissimulé, et la question
   « pourquoi je reçois ça alors que j'ai tout coupé ? » reviendrait sans fin.
   ═══════════════════════════════════════════════════════════════════════════ */

const THEMES = [
  { cle: 'light', libelle: 'Clair', icone: Sun, aide: 'Fond clair en permanence.' },
  { cle: 'dark', libelle: 'Sombre', icone: Moon, aide: 'Fond sombre en permanence.' },
  { cle: 'system', libelle: 'Système', icone: Monitor, aide: 'Suit le réglage de votre appareil.' },
];

const CANAUX = [
  { cle: 'inapp', libelle: 'Dans la console', icone: Bell },
  { cle: 'push', libelle: 'Notification push', icone: Smartphone },
  { cle: 'email', libelle: 'E-mail', icone: Mail },
];

/* L'ordre des catégories est celui dans lequel on s'en préoccupe : ce qui
   touche au compte et à la sécurité d'abord, l'activité ensuite. Le serveur
   les trie alphabétiquement, ce qui placerait « Accompagnement » avant
   « Sécurité » sans qu'aucune raison ne le justifie. */
const ORDRE_CATEGORIES = [
  'securite', 'compte', 'demande', 'candidature',
  'message', 'accompagnement', 'paiement',
];

const LIBELLE_CATEGORIE = {
  securite: 'Sécurité',
  compte: 'Compte',
  demande: 'Demandes',
  candidature: 'Candidatures',
  message: 'Messages',
  accompagnement: 'Accompagnement',
  paiement: 'Paiements',
};

function Bascule({ actif, disponible, verrouille, enCours, onChange, label }) {
  if (!disponible) {
    return (
      <span className="text-[12px] text-[var(--text-muted)]" title="Canal non prévu pour ce type">
        —
      </span>
    );
  }
  return (
    <button
      type="button"
      role="switch"
      aria-checked={actif}
      aria-label={label}
      aria-busy={enCours || undefined}
      disabled={verrouille}
      onClick={() => onChange(!actif)}
      className={`relative inline-flex w-10 h-6 rounded-controle transition-colors shrink-0 ${
        verrouille ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      }`}
      style={{ background: actif ? 'var(--accent)' : 'var(--border)' }}
    >
      <span
        aria-hidden="true"
        className="absolute top-1 w-4 h-4 rounded-controle bg-white transition-all"
        style={{ left: actif ? 20 : 4 }}
      />
    </button>
  );
}

export default function Settings({ user, theme, onSetTheme }) {
  /* Deux publics dans une meme page, et la ligne est nette : au-dessus, ce que
     chacun regle pour soi ; en dessous, ce qui vaut pour toute la plateforme.
     Un analyste consulte le catalogue sans pouvoir le corriger, un coach ne le
     voit pas — le serveur applique la meme regle, l'affichage ne fait que s'y
     conformer. */
  const role = user?.role || '';
  const voitLaConfiguration = ['SUPER_ADMIN', 'ADMIN', 'ANALYSTE'].includes(role);
  const peutConfigurer = ['SUPER_ADMIN', 'ADMIN'].includes(role);

  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  // Le code du type en cours d'enregistrement : la bascule concernée se fige,
  // les autres restent utilisables.
  const [enVol, setEnVol] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const d = await adminApi.getPreferencesNotification();
      setItems(Array.isArray(d?.items) ? d.items : []);
    } catch (e) {
      // L'objet entier : son statut dit lequel des cas afficher.
      setErreur(e);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const groupes = useMemo(() => {
    const parCategorie = new Map();
    items.forEach((it) => {
      const liste = parCategorie.get(it.categorie) || [];
      liste.push(it);
      parCategorie.set(it.categorie, liste);
    });
    const connues = ORDRE_CATEGORIES.filter((c) => parCategorie.has(c));
    // Une catégorie ajoutée au serveur et pas encore listée ici doit tout de
    // même apparaître, à la fin, plutôt que disparaître silencieusement.
    const autres = [...parCategorie.keys()].filter((c) => !ORDRE_CATEGORIES.includes(c));
    return [...connues, ...autres].map((c) => ({
      cle: c,
      libelle: LIBELLE_CATEGORIE[c] || c,
      types: parCategorie.get(c),
    }));
  }, [items]);

  const basculer = async (item, canal, valeur) => {
    setEnVol(item.code);
    // Affichage optimiste : la bascule répond tout de suite, et revient en
    // arrière si le serveur refuse. Attendre l'aller-retour donnerait
    // l'impression d'un interrupteur cassé.
    const avant = items;
    setItems((liste) => liste.map((i) => (i.code === item.code ? { ...i, [canal]: valeur } : i)));
    try {
      await adminApi.majPreferenceNotification({ code: item.code, [canal]: valeur });
      annoncerSucces('Préférence enregistrée.');
    } catch (e) {
      setItems(avant);
      annoncerErreur(e.message || "Ce réglage n'a pas pu être enregistré.");
    } finally {
      setEnVol('');
    }
  };

  const themeActif = theme === 'forest' ? 'dark' : (theme || 'light');

  return (
    <div className="space-y-6 animate-in">
      <div>
        <h1 className="titre-page">Paramètres</h1>
        <p className="text-[13px] text-[var(--text-muted)] mt-1 max-w-2xl leading-relaxed">
          Les réglages de la console et de la plateforme. Votre identité et
          votre mot de passe sont dans <span className="font-semibold">Mon profil</span>.
        </p>
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
        Mes préférences
      </p>

      {/* ── Apparence ─────────────────────────────────────────────────────── */}
      <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
        <h2 className="titre-carte text-[var(--text-bold)] flex items-center gap-2">
          <Palette className="w-4 h-4 text-[var(--accent-texte)]" aria-hidden="true" />
          Apparence
        </h2>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-5 max-w-xl leading-relaxed">
          Le choix est retenu sur cet appareil, pour votre compte uniquement.
        </p>

        <div role="radiogroup" aria-label="Thème de la console"
             className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {THEMES.map((t) => {
            const actif = themeActif === t.cle;
            return (
              <button
                key={t.cle}
                type="button"
                role="radio"
                aria-checked={actif}
                onClick={() => onSetTheme?.(t.cle)}
                className="act act-contour text-left"
                style={{ borderColor: actif ? 'var(--accent)' : 'var(--border)',
                  background: actif ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'transparent',
                  boxShadow: actif ? '0 0 0 1px var(--accent)' : 'none' }}
              >
                <t.icone className="w-5 h-5 mb-2"
                         style={{ color: actif ? 'var(--accent)' : 'var(--text-muted)' }}
                         aria-hidden="true" />
                <p className="text-[14px] font-semibold text-[var(--text-bold)]">{t.libelle}</p>
                <p className="text-[12px] text-[var(--text-muted)] mt-0.5 leading-relaxed">{t.aide}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Notifications ─────────────────────────────────────────────────── */}
      <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
        <h2 className="titre-carte text-[var(--text-bold)] flex items-center gap-2">
          <Bell className="w-4 h-4 text-[var(--accent-texte)]" aria-hidden="true" />
          Notifications
        </h2>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-5 max-w-2xl leading-relaxed">
          Choisissez par quels canaux vous être prévenu, événement par événement.
          Un réglage coupé ici ne vous retire jamais l'accès à l'information :
          elle reste consultable dans la page concernée.
        </p>

        {chargement && items.length === 0 ? (
          <SqueletteListe lignes={4} className="!shadow-none border border-[var(--border)] !rounded-pastille" />
        ) : erreur ? (
          <EtatErreur erreur={erreur} onReessayer={charger} compact />
        ) : items.length === 0 ? (
          <EtatSansDonnees cas="vide" compact icone={Bell} titre="Aucun type de notification"
            aide="Aucun événement n’est déclaré pour le moment : il n’y a rien à régler." />
        ) : (
          <div className="space-y-6">
            {groupes.map((groupe) => (
              <div key={groupe.cle}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  {groupe.libelle}
                </p>

                <div className="rounded-pastille border border-[var(--border)] overflow-hidden">
                  {/* L'en-tête des canaux ne se répète pas par catégorie sur
                      petit écran : chaque bascule porte son propre libellé
                      accessible, et la colonne est identifiée par son icône. */}
                  <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-[var(--border)]
                                  bg-[var(--bg-main)]">
                    <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      Événement
                    </span>
                    {CANAUX.map((c) => (
                      <span key={c.cle}
                            className="w-24 text-[11px] font-semibold uppercase tracking-wider
                                       text-[var(--text-muted)] text-center">
                        {c.libelle}
                      </span>
                    ))}
                  </div>

                  {groupe.types.map((item, i) => (
                    <div key={item.code}
                         className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 ${
                           i > 0 ? 'border-t border-[var(--border)]' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-medium text-[var(--text-bold)] flex items-center gap-2">
                          {item.libelle}
                          {item.critique && (
                            <span title="Ce type concerne votre compte ou votre sécurité : il ne peut pas être coupé."
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-controle text-[10px]
                                             font-semibold border border-[var(--border)] text-[var(--text-muted)]">
                              <Lock className="w-2.5 h-2.5" aria-hidden="true" />
                              Toujours actif
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        {CANAUX.map((c) => (
                          <div key={c.cle} className="w-24 flex sm:justify-center items-center gap-2">
                            <span className="sm:hidden text-[11px] text-[var(--text-muted)]">
                              {c.libelle}
                            </span>
                            <Bascule
                              actif={!!item[c.cle]}
                              disponible={!!item.canaux_possibles?.[c.cle]}
                              verrouille={item.critique || enVol === item.code}
                              enCours={enVol === item.code}
                              onChange={(v) => basculer(item, c.cle, v)}
                              label={`${c.libelle} — ${item.libelle}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {voitLaConfiguration && (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] pt-2">
            Configuration de la plateforme
          </p>

          <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
            <h2 className="titre-carte text-[var(--text-bold)] flex items-center gap-2">
              <Tags className="w-4 h-4 text-[var(--accent-texte)]" aria-hidden="true" />
              Formules tarifaires
            </h2>
            <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-5 max-w-2xl leading-relaxed">
              Le catalogue affiché sur la page publique des tarifs. Un prix
              corrigé ici s'applique immédiatement au site, au formulaire de
              demande et aux contrats à venir — les dossiers déjà enregistrés
              gardent le montant sous lequel ils ont été acceptés.
              {!peutConfigurer && ' Vous le consultez en lecture seule.'}
            </p>
            <FormulesTarifaires peutModifier={peutConfigurer} />
          </section>
        </>
      )}

      {/* ── Session ────────────────────────────────────────────────────────
          Ce n'est pas un réglage, c'est une règle. Elle est dite ici parce
          qu'une déconnexion automatique sans explication passe pour une
          panne. */}
      <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
        <h2 className="titre-carte text-[var(--text-bold)] flex items-center gap-2">
          <Lock className="w-4 h-4 text-[var(--accent-texte)]" aria-hidden="true" />
          Session
        </h2>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1 max-w-2xl leading-relaxed">
          Votre session se ferme automatiquement après <strong>30 minutes</strong> sans
          activité. Ce délai n'est pas modifiable : il protège les dossiers
          ouverts sur un poste laissé sans surveillance.
        </p>
      </section>
    </div>
  );
}
